/**
 * What the decision, offer and refinance cards read, in one place: the decision tools' card
 * (`underwriting/tools/decisions.py`, `DecisionTool.card`), the offer tools' results
 * (`underwriting/tools/offers.py`: `OfferSerializer`, `RefinanceSerializer`) and their approval
 * cards (`UpdateOfferTerms.approval_card`, `ProposeRefinanceOffer.approval_card`). Money and
 * percentages come as decimal strings (null when unknown); `num` reads them.
 */
import { displayFrequency } from "@/components/application/merchant-tasks/config/OfferTerms";
import {
  formatCurrency,
  formatPercentPoints,
} from "@/components/application/merchant-tasks/originationFee";
import type { DecisionStripProps } from "@/components/underwriting/DecisionStrip";
import type {
  ActivePosition,
  OfferValues,
} from "@/components/underwriting/UnderwritingAssessmentDialogs";
import type { ApplicationNotesDict, ApplicationUserNotes } from "@/types/borrower";
import { fmtMoneyShort } from "@/utils/optimisticAnalysisRecompute";

/** A decimal as the server sends it: a string, or a number from older or hand-built JSON. */
export type Decimal = string | number | null;

export function num(value: Decimal | undefined): number | null {
  if (value == null || value === "") return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

/** The statements analysis's strip (`quick_look.analysis_strip`, offers' `StripSerializer`). */
export interface AnalysisStrip {
  revenue_lines: { month: string; amount: number }[];
  average_revenue: number | null;
  /** Null for a merchant with no other funder to pay. */
  leverage: { percent: number; target_percent: number; monthly_payout: number } | null;
}

/** An offer's terms on a decision card (`decisions._card_terms`). */
export interface CardTerms {
  funding_amount: Decimal;
  factor: Decimal;
  /** In business days. */
  term: number | null;
  frequency: string | null;
  installment: Decimal;
  payback: Decimal;
  added_leverage: Decimal;
  origination_fee_percentage: Decimal;
}

export const DECISION_TOOLS = [
  "approve_application",
  "decline_application",
  "fund_application",
] as const;
export type DecisionTool = (typeof DECISION_TOOLS)[number];

/** One of the application's notes, as the approve dialog lists them (`decisions._existing_notes`). */
export interface ExistingNote {
  id: string | null;
  text: string | null;
  timestamp: string | null;
  note_type: string | null;
  user_name: string | null;
  user_role: string | null;
}

/**
 * A decision's card: the approval choice's `card` while it waits, the tool_result's `card` once
 * it ran (`status.from` null, `status.to` where the call left it).
 */
export interface DecisionCardPayload {
  kind: DecisionTool;
  version: number;
  application: { id: number; name: string };
  status: { from: string | null; to: string };
  strip: AnalysisStrip | null;
  terms: CardTerms | null;
  reasons: string[];
  /** The note the broker's email quotes; `stored`: the one already saved. */
  note: { text: string; stored: boolean } | null;
  // approve_application's approval card only: the Confirm Approve dialog's rows.
  positions?: { funder: string; amount: Decimal; frequency: string }[];
  position_number?: number;
  existing_notes?: ExistingNote[];
  /** How many older notes the card leaves out. */
  existing_notes_more?: number;
  /**
   * Whether the server offers Approve + Send Contract to this user on this application
   * (`approval.onboarding_card`); missing on an older card.
   */
  onboarding?: { offered: boolean; chosen?: boolean; label?: string; notice?: string };
  /** On the picked fake model (`Tool._card_with_overrides`). */
  fake_model?: boolean;
}

export function isDecisionCard(value: unknown): value is DecisionCardPayload {
  const card = value as Partial<DecisionCardPayload> | null;
  return (
    typeof card?.kind === "string" &&
    (DECISION_TOOLS as readonly string[]).includes(card.kind) &&
    typeof card.application?.id === "number"
  );
}

/** A money decimal as the offer sections print it ("$3,550.00"); "" when unknown. */
export function money(value: Decimal | undefined): string {
  const n = num(value);
  return n == null ? "" : formatCurrency(n);
}

// ─── offer tools (get_offer, update_offer_terms) ─────────────────────────────

/** The approval card version these cards draw; a call with another keeps the generic card. */
export const APPROVAL_CARD_VERSION = 1;

/** `OfferTermsSerializer`. */
export interface OfferTerms {
  funding_amount: Decimal;
  factor: Decimal;
  term: number | null;
  frequency: string;
  merchant_installment: Decimal;
  added_leverage: Decimal;
  origination_fee_percentage: Decimal;
  fee_is_default: boolean;
}

/** `OfferCostsSerializer`. */
export interface OfferCosts {
  payback: Decimal;
  origination_fee: Decimal;
  disbursed: Decimal;
  total_dollar_cost: Decimal;
  payment_periods: number | null;
  business_days_per_month: number;
}

/** `OfferSerializer`: an application's offer, what it costs, and the strip. */
export interface OfferPayload {
  application_id: number;
  app_status: string;
  decision: "approved" | "declined" | "pending" | null;
  deny_reasons: string[];
  updated_at: string | null;
  terms: OfferTerms | null;
  costs: OfferCosts | null;
  strip: AnalysisStrip | null;
}

export function isOfferPayload(value: unknown): value is OfferPayload {
  const offer = value as Partial<OfferPayload> | null;
  return typeof offer?.application_id === "number" && "terms" in offer && "costs" in offer;
}

/**
 * update_offer_terms' approval card: the offer as the edit saves it (`terms`, `costs`), the one
 * it replaces (`current`) and which terms differ (`changed`).
 */
export interface OfferApprovalCard {
  kind: "offer";
  version: number;
  application: { id: number; name: string };
  status: { from: string | null; to: string };
  offer_sha256: string;
  current: OfferTerms;
  terms: OfferTerms;
  changed: (keyof OfferTerms)[];
  costs: OfferCosts;
  strip: AnalysisStrip | null;
}

const isObject = (value: unknown) => typeof value === "object" && value !== null;

/** An approval card of `kind` these cards can draw: its terms, what they change and what before. */
const isApprovalCard = (value: unknown, kind: string, currentMayBeNull: boolean) => {
  const card = value as {
    kind?: unknown;
    version?: unknown;
    application?: { id?: unknown };
    terms?: unknown;
    changed?: unknown;
    current?: unknown;
  } | null;
  return (
    card?.kind === kind &&
    card.version === APPROVAL_CARD_VERSION &&
    typeof card.application?.id === "number" &&
    isObject(card.terms) &&
    Array.isArray(card.changed) &&
    (isObject(card.current) || (currentMayBeNull && card.current === null))
  );
};

export function isOfferApprovalCard(value: unknown): value is OfferApprovalCard {
  return isApprovalCard(value, "offer", false);
}

// ─── refinance tools (get_refinance_offer, propose_refinance_offer) ──────────

/** `RefinanceTermFieldsSerializer`. */
export interface RefinanceTerms {
  refi_amount: Decimal;
  balance: Decimal;
  factor: Decimal;
  merchant_installment: Decimal;
  term: number | null;
  frequency: string;
  origination_fee_percentage: Decimal;
  fee_is_default: boolean;
}

/** `RefinanceCostsSerializer`. */
export interface RefinanceCosts {
  payback: Decimal;
  origination_fee: Decimal;
  disbursed: Decimal;
  amount_disbursed: Decimal;
  net_renewal_funding: Decimal;
  total_dollar_cost: Decimal;
  payment_periods: number | null;
}

/** `RefinanceSerializer`: the refinance offer, what its contract costs, and the strip. */
export interface RefinancePayload {
  application_id: number;
  app_status: string;
  eligible: boolean;
  offer: (RefinanceTerms & { updated_at: string }) | null;
  costs: RefinanceCosts | null;
  send_ready: boolean;
  default_fee_percentage: Decimal;
  max_balance: Decimal;
  specified_percentage: Decimal;
  business_days_per_month: number;
  strip: AnalysisStrip | null;
}

export function isRefinancePayload(value: unknown): value is RefinancePayload {
  const refinance = value as Partial<RefinancePayload> | null;
  return (
    typeof refinance?.application_id === "number" && "eligible" in refinance && "offer" in refinance
  );
}

/**
 * propose_refinance_offer's approval card: the offer as the save stores it, the one it replaces
 * (`current`, null for a new offer) and which terms differ. No status move.
 */
export interface RefinanceApprovalCard {
  kind: "refinance";
  version: number;
  application: { id: number; name: string };
  /** The status it is in (a save moves none); absent from cards drawn before it was sent. */
  status?: { from: string | null; to: string };
  offer_version: string;
  current: RefinanceTerms | null;
  terms: RefinanceTerms;
  changed: (keyof RefinanceTerms)[];
  costs: RefinanceCosts;
  strip: AnalysisStrip | null;
}

export function isRefinanceApprovalCard(value: unknown): value is RefinanceApprovalCard {
  // A new refinance offer replaces none.
  return isApprovalCard(value, "refinance", true);
}

// ─── what an edit changes ────────────────────────────────────────────────────

/** One changed term, as "Funding amount: $35,500.00 → $32,000.00". */
export interface TermChange {
  label: string;
  before: string;
  after: string;
}

const percent = (value: Decimal) => {
  const n = num(value);
  return n == null ? "" : `${formatPercentPoints(n)}%`;
};

/** Each term's label and reading, as the offer sections name them. */
const TERM_READERS: Record<string, [string, (value: Decimal) => string]> = {
  funding_amount: ["Funding amount", money],
  refi_amount: ["Funding amount", money],
  balance: ["Prior balance", money],
  factor: ["Factor", (value) => (num(value) == null ? "" : String(num(value)))],
  merchant_installment: ["Payment", money],
  term: ["Term", (value) => (num(value) == null ? "" : `${num(value)} days`)],
  frequency: ["Frequency", (value) => displayFrequency(String(value ?? ""))],
  added_leverage: ["Added leverage", percent],
  origination_fee_percentage: ["Origination fee", percent],
};

/** The terms an approval card's edit changes, before and after (none for a new offer). */
export function termChanges(card: OfferApprovalCard | RefinanceApprovalCard): TermChange[] {
  const { current, terms } = card;
  if (!isObject(current) || !isObject(terms) || !Array.isArray(card.changed)) return [];
  const before = current as unknown as Record<string, Decimal>;
  const after = terms as unknown as Record<string, Decimal>;
  return card.changed.flatMap((field) => {
    const reader = TERM_READERS[field];
    if (!reader) return [];
    const [label, read] = reader;
    return [{ label, before: read(before[field]) || "—", after: read(after[field]) || "—" }];
  });
}

// ─── shared readers ──────────────────────────────────────────────────────────

/** The leverage figures when the strip has none: the dashboard's own fallback target. */
const DEFAULT_TARGET_PCT = 30;

/** The strip as DecisionStrip draws it: the dashboard's short money, no by-funder payout. */
export function stripProps(strip: AnalysisStrip): DecisionStripProps {
  return {
    revenueLines: strip.revenue_lines.map((line) => ({
      month_abbrev: line.month,
      amount: line.amount,
      amount_display: fmtMoneyShort(line.amount),
    })),
    avgRevenue: strip.average_revenue == null ? null : fmtMoneyShort(strip.average_revenue),
    leveragePct: strip.leverage?.percent ?? 0,
    targetPct: strip.leverage?.target_percent ?? DEFAULT_TARGET_PCT,
    monthlyPayout: strip.leverage?.monthly_payout ?? 0,
    payoutByFunder: [],
  };
}

/** Offer Builder values (`OfferCalculator`'s `initialValues`) from an offer's terms. */
export interface CalculatorTerms {
  fundingAmount?: number;
  factor?: number;
  installment?: number;
  term?: number;
  frequency?: string;
  addedLeverage?: number;
}

export function calculatorTerms(terms: {
  funding_amount: Decimal;
  factor: Decimal;
  term: number | null;
  frequency: string | null;
  installment?: Decimal;
  merchant_installment?: Decimal;
  added_leverage: Decimal;
}): CalculatorTerms {
  const values: CalculatorTerms = {
    fundingAmount: num(terms.funding_amount) ?? undefined,
    factor: num(terms.factor) ?? undefined,
    installment: num(terms.installment ?? terms.merchant_installment ?? null) ?? undefined,
    term: terms.term ?? undefined,
    frequency: terms.frequency || undefined,
    addedLeverage: num(terms.added_leverage) ?? undefined,
  };
  return Object.fromEntries(
    Object.entries(values).filter(([, value]) => value !== undefined)
  ) as CalculatorTerms;
}

// ─── the Confirm Approve dialog's rows (AcceptDialog) ────────────────────────

/** The dialog's Existing Positions, from the approve card. */
export function dialogPositions(card: DecisionCardPayload): ActivePosition[] {
  return (card.positions ?? []).map((position) => ({
    funder: position.funder,
    amount: num(position.amount) ?? 0,
    frequency: position.frequency,
  }));
}

/** "Coming in as the 3rd position", as the dashboard words it. */
export function ordinalPosition(card: DecisionCardPayload): string | undefined {
  const n = card.position_number;
  if (!n) return undefined;
  const suffix = n === 1 ? "st" : n === 2 ? "nd" : n === 3 ? "rd" : "th";
  return `Coming in as the ${n}${suffix} position`;
}

/** The dialog's Our Offer grid: the card's terms, zero where it has none. */
export function dialogOffer(terms: CardTerms | null): OfferValues {
  return {
    fundingAmount: num(terms?.funding_amount ?? null) ?? 0,
    factor: num(terms?.factor ?? null) ?? 0,
    installment: num(terms?.installment ?? null) ?? 0,
    term: terms?.term ?? 0,
    frequency: terms?.frequency ?? "daily",
    addedLeverage: num(terms?.added_leverage ?? null) ?? 0,
  };
}

const NOTE_ROLES: ApplicationUserNotes["userRole"][] = ["admin", "underwriter", "broker", "system"];

/** The card's notes as the application keeps them (by writer), for the dialogs' Existing Notes. */
export function dialogNotes(card: DecisionCardPayload): ApplicationNotesDict | undefined {
  const notes = card.existing_notes ?? [];
  if (!notes.length) return undefined;
  const byWriter: ApplicationNotesDict = {};
  notes.forEach((note, index) => {
    if (!note.text) return;
    const role = NOTE_ROLES.find((candidate) => candidate === note.user_role) ?? "system";
    const key = `${note.user_name ?? ""}:${role}`;
    byWriter[key] ??= { userName: note.user_name ?? "", userRole: role, notes: [] };
    byWriter[key].notes.push({
      id: note.id ?? `note-${index}`,
      text: note.text,
      timestamp: note.timestamp ?? "",
      ...(note.note_type === "automated" || note.note_type === "user"
        ? { noteType: note.note_type }
        : {}),
    });
  });
  return byWriter;
}
